// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { message } from 'antd';
import { setRoles } from '../helpers/roleStore';
import { clearRecentUsers } from '../helpers/recentUsers';

//todo - incorporate this somehwere in this file
/*

const VALID_ROLES = [
  'ROLE_SUPPORT_MANAGER',
  'ROLE_SUPPORT_LEAD',
  'ROLE_SUPPORT_AGENT_3',
  'ROLE_SUPPORT_AGENT_2',
  'ROLE_SUPPORT_AGENT',
];

// Checks if the user has at least one valid role
function isValidUserRole(userData) {
  return userData?.roles?.some((role) => VALID_ROLES.includes(role)) || false;
}

// this is how the above was used in App.js
        if (
            userEmail?.includes(ALLOWED_EMAIL_DOMAIN) &&
            isValidUserRole(parsedUserData)
        ) {
          setUser(firebaseUser);
          setLoginError(null);
          // Store roles if needed
          localStorage.setItem(
              'user_roles',
              JSON.stringify(parsedUserData?.roles || [])
          );
        } else {
          // Not valid => sign out
          setLoginError('Please login with a valid email address');
          await signOutAndClear();
        }




 */

/**
 * parseJwt: Utility function to parse JWT (if you need to read roles or custom claims).
 * If you have this in a separate helper file, import it instead.
 */
function parseJwt(token) {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (err) {
        message.error('Unable to parse JWT Token.');
        return null;
    }
}

// Create the context
const AuthContext = createContext(null);

/**
 * AuthProvider component
 * - Listens to Firebase auth changes via onAuthStateChanged
 * - Fetches a token in memory if user is logged in, parses roles, and sets state
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loginError, setLoginError] = useState(null);

    useEffect(() => {
        const unsubscribe = firebase.auth().onIdTokenChanged(async (firebaseUser) => {
            if (!firebaseUser) {
                // User is signed out
                clearRecentUsers();
                setUser(null);
                setUserRoles([]);
                setRoles([]); // <== Clear inMemory roles
                setLoading(false);
                return;
            }

            try {
                // Get the user's ID token in memory (no localStorage)
                const token = await firebaseUser.getIdToken(/* forceRefresh */ false);
                const decoded = parseJwt(token) || {};

                // Extract roles
                const roles = decoded.roles || [];

                // Example domain check
                const email = (firebaseUser.email || '').toLowerCase();
                // todo: technically we can check if the roles are kosher here as well -- see todo above
                if (!email.endsWith('@meetava.com')) {
                    // Not valid for your admin app => sign out
                    setLoginError('Please use a valid @meetava.com account');
                    await firebase.auth().signOut();
                    setUser(null);
                    setUserRoles([]);
                    setRoles([]); // <== Clear inMemory roles
                } else {
                    console.log('firebaseUser', firebaseUser);
                    setUser({email: firebaseUser.email, uid: firebaseUser.uid});
                    setUserRoles(roles);
                    setRoles(roles); // <==  Sync to the inMemoryRoles store
                    setLoginError(null);
                }
            } catch (err) {
                console.error('Error fetching token or parsing roles:', err);
                setLoginError('Authentication Error');
                // If token fetch fails, sign out
                await firebase.auth().signOut();
                setUser(null);
                setUserRoles([]);
                setRoles([]); // <== Clear inMemory roles
            } finally {
                setLoading(false);
            }
        });

        // Cleanup on unmount
        return () => unsubscribe();
    }, []);

    // Show any login error once
    useEffect(() => {
        if (loginError) {
            message.error(loginError);
        }
    }, [loginError]);

    // Provide a signOut helper
    const signOut = async () => {
        await firebase.auth().signOut();
        setUser(null);
        setUserRoles([]);
        setRoles([]); // <== Clear inMemory roles
    };

    return (
        <AuthContext.Provider value={{ user, userRoles, loading, signOut }}>
            {children}
        </AuthContext.Provider>
    );
}

// Custom hook for easy access
export function useAuth() {
    return useContext(AuthContext);
}
